/** @jsx isolateComponent */
import { css } from '@emotion/core';

import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

import { Animation, Colors } from '../../../theme';
import type { ColorName } from '../../../theme';
import { Icon } from '../../display/Icon';
import type { IconName } from '../../display/Icon';

type Props = {
  /**
   * The base color of the icon. The color of the circles are
   * calculated based on this color.
   */
  color: ColorName;
  /**
   * The name of the icon to render.
   */
  name: IconName;
  /**
   * Multiple sizes are supported. Specify which one you want.
   */
  size: 'xsmall' | 'small' | 'medium' | 'large';
};

const Styles = labelStyles('IconCircle', {
  root: (props: Props) => [
    css`
      // This white bottom layer is necessary to prevent potential
      // bleed-through from any content underneath.
      background-color: ${Colors.white};
      border-radius: 100%;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${Colors[props.color]};
    `,

    withModifier(
      props.size,
      {
        xsmall: css`
          height: 40px;
          width: 40px;
        `,
        small: css`
          height: 70px;
          width: 70px;
        `,
        medium: css`
          height: 90px;
          width: 90px;
        `,
        large: css`
          height: 120px;
          width: 120px;
        `,
      }[props.size]
    ),
  ],

  circle: css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 100%;
    transition: ${Animation.transitionValue};
    background-color: currentColor;
  `,

  outerCircle: css`
    opacity: 0.15;
  `,

  innerCircle: css`
    opacity: 0.4;
    transform: scale(0.66666);
  `,
});

/**
 * `Icon` component that renders within a circle.
 */
export const IconCircle = (props: Props) => {
  return (
    <div css={Styles.root(props)}>
      <div css={[Styles.circle, Styles.outerCircle]}></div>
      <div css={[Styles.circle, Styles.innerCircle]}></div>
      <Icon
        color={props.color}
        name={props.name}
        size={{ xsmall: 18, small: 30, medium: 40, large: 50 }[props.size]}
      />
    </div>
  );
};

IconCircle.defaultProps = {
  color: 'grey700',
  size: 'medium',
};
